import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Pencil, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", active: true };

export default function DepartmentsPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/departments").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const open = (d) => { setEditing(d || {}); setForm(d ? { name: d.name, description: d.description || "", active: d.active } : EMPTY); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Informe o nome"); return; }
    try {
      if (editing.id) await api.patch(`/departments/${editing.id}`, form);
      else await api.post("/departments", form);
      toast.success("Setor salvo");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar"); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Excluir o setor ${d.name}?`)) return;
    try { await api.delete(`/departments/${d.id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha ao excluir"); }
  };

  return (
    <div className="h-full overflow-auto p-6 lg:p-10">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="heading text-3xl font-extrabold flex items-center gap-2"><Building2 className="w-7 h-7 text-emerald-600" /> Setores</h1>
            <p className="text-sm text-muted-foreground mt-1">Filas de atendimento da Lexcon. Vincule atendentes em Atendentes.</p>
          </div>
          <Button onClick={() => open(null)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="department-new-button">
            <Plus className="w-4 h-4 mr-2" /> Novo setor
          </Button>
        </div>
        <Card>
          <CardContent className="p-0">
            {items.map((d) => (
              <div key={d.id} className="flex items-center gap-4 p-4 border-b border-border" data-testid="department-row">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold flex items-center gap-2">
                    {d.name}
                    {!d.active && <Badge variant="secondary" className="text-[10px]">Inativo</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{d.description || "—"}</div>
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-1"><Users className="w-3 h-3" /> {d.members}</div>
                <Button size="sm" variant="ghost" onClick={() => open(d)} data-testid="department-edit-button"><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(d)} data-testid="department-delete-button"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Editar setor" : "Novo setor"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="department-name-field" /></div>
            <div><Label>Descrição (ajuda a IA a encaminhar)</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /> <span className="text-sm">Ativo</span></div>
          </div>
          <DialogFooter><Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="department-save-button">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
